// React context for keeping track of users authenticated state using session cookies
// context also provides functions for login in and out
import dynamic from "next/dynamic";
import React, { ReactElement, useCallback, useEffect } from "react";

import { NODE_ENV } from "@shared/env";

import usePublicUserClaimService from "@app-contexts/public/features/claims/data/services/usePublicUserClaimService";
import { CombinedFulfillClaimType } from "@app-contexts/public/features/claims/data/types";
import { AuthProfileRepository } from "@shared/modules/auth/AuthProfileRepository";
import useAuthService from "@shared/modules/auth/hooks/useAuthService";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { QueryKeyManager } from "@shared/services/api/hooks/utils/queryKeyManager";
import { getAxiosErrorMessage } from "@shared/utils/api";
import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import axios from "axios";
import Cookies from "js-cookie";
import { useQuery } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { AuthFactory } from "../AuthFactory";
import useRecaptcha from "../hooks/useRecaptcha";
import {
  AuthProfileSerializer,
  FulfillClaimRequestPayloadType,
  LoginResponse,
  PublicClaimUserUUIDSerializerType,
  RegistrationPayload,
} from "../serializer-types";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore react-query/devtools removes children as a default prop on the FC type per React 18, but we are currently on React 17 which still has children as a default prop on the FC type.
// So TSC will fail to compile. @see https://stackoverflow.com/questions/73326859/getting-error-type-children-element-has-no-properties-in-common-with-ty
const ReactQueryDevtoolsProduction = dynamic(() =>
  import("react-query/devtools/development").then((d) => d.ReactQueryDevtools)
);

type UserSessionAuthStateContextType = {
  isLoggedIn?: boolean;
  userProfileData: undefined | AuthProfileSerializer;
  isLoading: boolean;
  isRefetching?: boolean;
  createAnAccount: (payload: RegistrationPayload) => Promise<LoginResponse>;
  login: (
    email: string,
    password: string,
    otp?: string
  ) => Promise<LoginResponse | { requires_otp: boolean } | undefined>;
  logout: () => Promise<{ detail: string } | undefined>;
  fulfillClaim: (
    payload: FulfillClaimRequestPayloadType
  ) => Promise<PublicClaimUserUUIDSerializerType>;
  setLoggedInStates?: () => void;
};

const UserSessionAuthStateContext = React.createContext<
  UserSessionAuthStateContextType | undefined
>(undefined);

const userCacheKey = "showpass-user";
export const userFlagLogoutKey = "showpass-flag-logout";
export const userAuthStateKey = "showpass-logged-in";
export const userWebviewAuthToken = "showpass-webview-auth-token";

const auth = AuthFactory.createAuth();
const notifier = NotifierFactory.createNotifier();

export const UserSessionAuthStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}): ReactElement => {
  const [isLoggedIn, setIsLoggedIn] = React.useState<boolean>(); //  Initially undefined. Will be set based on cookies and logout flags after the component mounts.
  // This state ensures children don't render until the webview auth token check is complete.
  // Without this, children could mount and make API calls before the webview auth token is checked and header is set in the useEffect.
  // This would result in unauthorized API calls failing, as the Authorization header wouldn't be set yet.
  const [isWebviewAuthTokenChecked, setIsWebviewAuthTokenChecked] =
    React.useState<boolean>(false);
  const [showDevtools, setShowDevtools] = React.useState<boolean>();
  const { useRegistration, removeAllRepositoryQueries } = useAuthService();
  const { mutateAsync: registration } = useRegistration();

  const setLoggedInStates = () => {
    Cookies.set(userAuthStateKey, "true");
    setIsLoggedIn(true);
  };

  const setLoggedOutStates = () => {
    Cookies.set(userAuthStateKey, "false");
    Cookies.set(userWebviewAuthToken, "");

    setIsLoggedIn(false);

    localStorage.removeItem(userCacheKey);

    removeAllRepositoryQueries();
  };

  // This effect handles webview authentication by checking for a JWT token in cookies.
  // If a token exists, it sets the Authorization header for all subsequent axios requests
  // and marks the user as logged in. The cleanup function removes the Authorization header
  // when the component unmounts to prevent token leakage.
  useEffect(() => {
    const token = Cookies.get(userWebviewAuthToken);

    if (token) {
      axios.defaults.headers.common.Authorization = `JWT ${token}`;
      setLoggedInStates();
    }

    // Mark that webview auth token check is complete and header set (if token existed)
    // This allows children to render, knowing the webview auth state is ready
    setIsWebviewAuthTokenChecked(true);

    return () => {
      if (axios.defaults?.headers?.common) {
        delete axios.defaults.headers.common.Authorization;
      }
    };
  }, []);

  const { executeRecaptcha } = useRecaptcha();
  const authProfileRepo = new AuthProfileRepository();
  const { useCreate } = usePublicUserClaimService();
  const { mutateAsync } = useCreate();

  // try to get user profile and cache it
  // if not set them as logged out
  // we check it all the time since it's still possible to login
  // from web-app
  const queryKeyManager = new QueryKeyManager(authProfileRepo);
  const key = queryKeyManager.generateKey(["retrieve"]);
  const {
    refetch,
    data: userProfileData,
    isRefetching,
    isLoading,
  } = useQuery<AuthProfileSerializer | undefined>(
    key,
    () =>
      authProfileRepo
        .retrieve(undefined, { showToastOnError: false })
        .then((response) => {
          setLoggedInStates();
          if (isStorageAvailable("localStorage")) {
            localStorage.setItem(userCacheKey, JSON.stringify(response));
          }
          return response;
        })
        .catch(() => {
          setLoggedOutStates();
          return undefined;
        }),
    {
      // Disable this call on testing.
      // Boolean(isLoggedIn) is to handle the initial undefined state.
      enabled: NODE_ENV !== "test" && isLoggedIn !== false,
      initialData: () =>
        isStorageAvailable("localStorage")
          ? JSON.parse(localStorage.getItem(userCacheKey) || "false")
          : false,
      // https://react-query-v3.tanstack.com/guides/initial-query-data#staletime-and-initialdataupdatedat
      refetchOnWindowFocus: false,
      // Only notify this hook when the data prop changes
      notifyOnChangeProps: ["data"],
    }
  );

  const createAnAccount = async (
    payload: RegistrationPayload
  ): Promise<LoginResponse> => {
    const { data } = await registration(payload);
    await refetch();

    return data;
  };

  const login = useCallback(
    async (
      email: string,
      password: string,
      otp?: string
    ): Promise<
      | LoginResponse
      | { requires_otp: boolean; otp_expiry_step: number }
      | undefined
    > => {
      try {
        const recaptchaToken = await executeRecaptcha("account_login_nextjs");
        const data = await auth.login(email, password, {
          otp_token: otp,
          captcha_token: recaptchaToken,
        });
        await refetch();

        return data as LoginResponse | undefined;
      } catch (error: unknown) {
        // Type narrowing for the error
        if (axios.isAxiosError(error)) {
          const errorCode = error?.response?.data?.error_code;
          if (Array.isArray(errorCode)) {
            if (errorCode.includes(2994)) {
              // Handle OTP error
              const otp_expiry_step = Number(
                error?.response?.data?.additional_info?.step
              );
              return { requires_otp: true, otp_expiry_step };
            }
          } else {
            const errorMessage = getAxiosErrorMessage(error);
            notifier.error(errorMessage);
          }
        }
        throw error; // Re-throw the error after handling it
      }
    },
    [auth, executeRecaptcha, refetch]
  );

  const logout = async () => {
    const data = await auth.logout();
    if (data) {
      setLoggedOutStates();
    }
    return data;
  };

  const fulfillClaim = async (
    payload: CombinedFulfillClaimType
  ): Promise<PublicClaimUserUUIDSerializerType> => {
    const data = await mutateAsync(payload);
    await refetch();
    return data as PublicClaimUserUUIDSerializerType;
  };

  const isShowpassStaff =
    isLoggedIn && userProfileData && userProfileData.is_staff;
  // Restrict live deployment to Showpass staff, allow permissions for preview and beta.
  const getDevtoolsPermissions = () => {
    if (NODE_ENV === "production") {
      return isShowpassStaff;
    }

    return true;
  };

  React.useEffect(() => {
    const isLoggedInCache = Cookies.get(userAuthStateKey);
    const flaggedForLogout = Cookies.get(userFlagLogoutKey);

    // when a user hits /accounts/logout/ the server sets this flag
    // and then redirects them to the homepage. Then this condition will get
    // evaluated and log the user out and remove the flag
    // this was done to support logging out from the old dashboard
    if (flaggedForLogout === "true") {
      logout();

      Cookies.remove(userFlagLogoutKey);
      return;
    }

    if (isLoggedInCache) {
      setIsLoggedIn(isLoggedInCache === "true");
    }
  }, []);

  // Adds RQ Devtools to preview/beta/production
  React.useEffect(() => {
    if (window) {
      /* eslint-disable @typescript-eslint/ban-ts-comment */
      if (getDevtoolsPermissions()) {
        // @ts-ignore
        window.toggleDevtools = () => setShowDevtools((prev) => !prev);
        // @ts-ignore
      } else if (window.toggleDevtools) {
        // @ts-ignore
        delete window.toggleDevtools;
      }
      /* eslint-disable @typescript-eslint/ban-ts-comment */
    }
  }, [userProfileData, isLoggedIn]);

  const value = {
    isLoggedIn,
    userProfileData,
    isLoading,
    isRefetching,
    createAnAccount,
    login,
    logout,
    fulfillClaim,
    setLoggedInStates,
  };

  return (
    <UserSessionAuthStateContext.Provider value={value}>
      {(Cookies.get(userWebviewAuthToken) && isWebviewAuthTokenChecked) ||
      !Cookies.get(userWebviewAuthToken) ? (
        <>
          {/* Devtools for local */}
          <ReactQueryDevtools />
          {/* Devtools for preview/beta/production */}
          {getDevtoolsPermissions() && showDevtools && (
            <ReactQueryDevtoolsProduction />
          )}
          {children}
        </>
      ) : null}
    </UserSessionAuthStateContext.Provider>
  );
};

export const useUserSessionAuthState = (): UserSessionAuthStateContextType => {
  const context = React.useContext(UserSessionAuthStateContext);
  if (context === undefined) {
    throw new Error(
      "useUserSessionAuthState must be used within a UserSessionAuthStateProvider"
    );
  }
  return context;
};
